// Tipificaciones de tickets de Ops — transcripción FIEL del catálogo del CRM
// (crm/packages/contracts/src/ticketTypification.ts, decisión 2026-07-16).
//
// Espejo EXACTO de las propiedades de HubSpot: los VALORES son los internos de
// HubSpot tal cual (trim aplicado) para que un ticket creado en Operations y uno
// sincronizado desde HubSpot sean el mismo dato filtrable. NO renombrar valores
// sin migrar los datos ya persistidos.
//
// Modelo de DOS niveles INDEPENDIENTES:
// - Opción 1 (`incidentType`) = propiedad global `tipo_incidencia_altas`. Se
//   ofrece en TODOS los pipelines ES (tengan o no Opción 2).
// - Opción 2 (`typification`) = una lista POR PIPELINE (mapa pipeline→grupos).
//   Los pipelines sin entrada aquí no tienen Opción 2.
//
// El mapeo pipeline→lista se casa por el ID de pipeline del CRM (`ticket_pipelines.id`,
// p.ej. `modificaciones`, `alta_poliza`), que es justo el `pipelineId` que
// Operations recibe en cada ticket/summary y en el selector de alta.

// Opción 1 — "Tipo Incidencia Tickets Ops" (30 opciones).
export const TICKET_INCIDENT_TYPES = [
  "Error Info Póliza",
  "Documentación Adicional",
  "Emisión de póliza",
  "Modificación Beneficiarios",
  "Modificación Pagos",
  "Modificación Capital",
  "Modificación Garantías",
  "Modificación Profesión o Deportes",
  "Modificación de Datos personales",
  "Modificación de Tomador",
  "Cancelación Impago",
  "Cancelación sin efecto",
  "Cancelación desistimiento",
  "Cancelación estándard",
  "Cancelación Renovación",
  "Rechazado por la Aseguradora",
  "Rechazado por Life 5",
  "Portabilidad Realizada",
  "Portabilidad No Realizada",
  "Siniestro Fallecimiento",
  "Siniestro IPA",
  "Impago ( CARD)",
  "Impago ( SEPA)",
  "Pagos",
  "Promociones",
  "Sistema Life 5",
  "Sistema AXA",
  "Info Póliza",
  "Otras",
  "Rehabilitación póliza",
] as const;

// Grupo de opciones de la Opción 2. Un pipeline suele tener UN grupo (una
// propiedad HubSpot); `alta_poliza` tiene DOS (Altas Nuevas + Portabilidad) que
// la UI pinta como <optgroup> de un mismo select.
export type TicketTypificationGroup = {
  /** Label del grupo (solo se muestra si el pipeline tiene >1 grupo). */
  label: string;
  /** Propiedad HubSpot de origen (metadato; no se pinta). */
  hubspotProperty: string;
  options: readonly string[];
};

const MESES_IMPAGO = ["1 MES", "2 MESES", "3 MESES", "4 MESES", "Prima Anual"] as const;

// Opción 2 por pipeline CRM (ids de `ticket_pipelines`).
export const TICKET_TYPIFICATIONS_BY_PIPELINE: Record<
  string,
  readonly TicketTypificationGroup[]
> = {
  modificaciones: [
    {
      label: "Modificaciones",
      hubspotProperty: "tipificacion_modificaciones",
      options: [
        "Modificación de beneficiarios Excluyente",
        "Modificación de beneficiarios Libre designación",
        "Modificación de beneficiarios Pignoraticia",
        "Modificación de beneficiarios Herederos Legales",
        "Modificación de beneficiarios Orden de prelación",
        "Modificación de Tarjeta",
        "Modificación SEPA",
        "Modificación Tarjeta a SEPA",
        "Modificación SEPA a Tarjeta",
        "Periodicidad Anual a Mensual",
        "Periodicidad Mensual a Anual",
        "Aumentar Capital",
        "Reducir Capital",
        "Incluir IPA",
        "Quitar IPA",
        "Modificación de Profesión ( con Riesgo)",
        "Modificación de Profesión ( sin Riesgo)",
        "Incluir Deporte",
        "Quitar Deporte",
        "Modificar Dirección",
        "Modificación datos personales beneficiario",
        "Modificación Teléfono",
        "Modificación Correo electrónico",
        "Modificación del Tomador o datos del Tomador",
      ],
    },
  ],
  cancelaciones: [
    {
      label: "Cancelaciones",
      hubspotProperty: "tipificacion_cancelaciones",
      options: [
        "Cancelación Impago",
        "Información incorrecta",
        "Cliente no está conforme con el precio de Renovación",
        "Cliente elige competencia",
        "Cliente no está conforme con las condiciones",
        "Cancelación Omisión de Patologías",
        "Cliente NO Firma",
        "No aporta ID",
        "Cliente no está conforme con la experiencia usuario",
        "Cliente cancela por motivos personales (economía, no necesita, etc)",
        "Cúmulo de Capital",
        'Patologías / Errores internos de Funnel / " Fairy Clients"',
        "Aumento de Capital",
        "Bajada de Capital",
      ],
    },
  ],
  alta_poliza: [
    {
      label: "Altas nuevas",
      hubspotProperty: "tipificacion_altas_nuevas",
      options: [
        "Firma No Admisible",
        "Nombre / Apellidos Mal",
        "Error de Género / Sexo",
        "Error en Patologías declaradas",
        "Error en CSV / No se genera",
        "Solicitud ID",
        "AXA Solicita CSV",
        "PRP (Persona de Responsabilidad Pública)",
        "Informe Financiero / Salud / KYC",
        "Subir Póliza a Admin",
        "No Cesión de Datos",
        "Error en la emisión de AXA",
        "Telesuscripción AXA",
        "Portabilidad",
        "DISCREPANCIAS PRIMAS/ DATOS",
      ],
    },
    {
      label: "Portabilidad",
      hubspotProperty: "tipificacion_portabilidad",
      options: [
        "Portabilidad Realizada",
        "Portabilidad No Realizada por estar fuera de plazo",
        "Portabilidad No realizada por solicitud del cliente.",
      ],
    },
  ],
  another_issue: [
    {
      label: "Another issue",
      hubspotProperty: "tipificacion_another_issue",
      options: [
        "Incidencias Pagos Clientes",
        "Incidencias Pagos Internos Sistema",
        "Incidencias Pagos AXA",
        "Incidencias Pagos Bloqueados",
        "Incidencias Recibos AXA",
        "Promociones / Incidencias promociones",
        "Incidencias Sistema Life 5 / Funnel, etc",
        "Incidencias Renovaciones",
        "Incidencias Patologías",
        "Incidencia Queja Cliente",
        "Incidencias Info Póliza",
      ],
    },
  ],
  impago_tarjeta: [
    {
      label: "Impago tarjeta",
      hubspotProperty: "tipificacion_payments_failed",
      options: MESES_IMPAGO,
    },
  ],
  disputados_sepa: [
    {
      label: "Disputados SEPA",
      hubspotProperty: "tipificacion_disputed_charges",
      options: MESES_IMPAGO,
    },
  ],
  siniestro_fallecimiento: [
    {
      label: "Siniestro",
      hubspotProperty: "claims",
      options: ["Fallecimiento", "Incapacidad Permanente Absoluta", "Dolo / Fraude"],
    },
  ],
};

// Pipelines ES donde aplica la Opción 1 (`tipo_incidencia_altas` se rellena en
// todos los pipelines ES, tengan o no Opción 2 propia).
export const TICKET_INCIDENT_TYPE_PIPELINES: readonly string[] = [
  "alta_poliza",
  "cancelaciones",
  "modificaciones",
  "disputados_sepa",
  "impago_tarjeta",
  "suscripcion_axa",
  "corredores",
  "siniestro_fallecimiento",
  "fairy",
  "another_issue",
];

// ¿El pipeline ofrece Opción 1 (Tipo de incidencia)?
export function ticketPipelineHasIncidentType(pipelineId: string): boolean {
  return TICKET_INCIDENT_TYPE_PIPELINES.includes(pipelineId);
}

// Grupos de Opción 2 del pipeline ([] si no tiene). Helper equivalente al
// `ticketTypificationGroups` del CRM.
export function typificationsForPipeline(
  pipelineId: string,
): readonly TicketTypificationGroup[] {
  return TICKET_TYPIFICATIONS_BY_PIPELINE[pipelineId] ?? [];
}

// Opciones planas de Opción 2 del pipeline ([] si no tiene).
export function ticketTypificationOptions(pipelineId: string): string[] {
  return typificationsForPipeline(pipelineId).flatMap((g) => [...g.options]);
}
